import { AppThunk } from "../store";
import { createAsyncThunk, ThunkDispatch } from "@reduxjs/toolkit";
import { ScheduleState } from "./scheduleSlice";
import { actionReducerItems, dateInReloadRange, getDateKey } from "../common/commonActions";
import { loadRdsMeeting, loadRdsMeetings, mustReloadRdsMeetings } from "../rds/rdsActionsMeeting";
import { loadRdsEvent, loadRdsEvents } from "../rds/rdsActionsEvent";
import { rdsEvent_Dto } from "../../interfaces/dtos/rdsEvent";
import { ScheduleEvent } from "../../interfaces/dtos/schedule";

///////////////////////////////////////////////////////////////////////////////////////////////////
// Side menu
///////////////////////////////////////////////////////////////////////////////////////////////////
export const loadSideMenuData = (
    date: Date, categoryCode: string, meetingIdsToLoadEvents?: number[]
): AppThunk =>
    async (dispatch, getState) => {

        await actionScheduleForSideMenu(dispatch, getState().schedule, async () => {

            if (mustReloadRdsMeetings(date, categoryCode, getState().rds)) {
                await dispatch(loadRdsMeetings({ date, categoryCode, meetingIdsToLoadEvents }));
            }

        })

    };

///////////////////////////////////////////////////////////////////////////////////////////////////
// Load Side Menu from Event
///////////////////////////////////////////////////////////////////////////////////////////////////
export const loadSideMenuFromEvent = (eventId: number): AppThunk =>
    async (dispatch, getState) => {

        let event = getState().rds.rdsEvents.find((e) => e.id === eventId);
        if (!event) {
            await dispatch(loadRdsEvent(eventId));
        }
        event = getState().rds.rdsEvents.find((e) => e.id === eventId);
        if (!event) { return; }

        let meeting = getState().rds.rdsMeetings.find((m) => m.id === event.meetingId);
        if (!meeting) {
            await dispatch(loadRdsMeeting(event.meetingId));
        }
        meeting = getState().rds.rdsMeetings.find((m) => m.id === event.meetingId);
        if (!meeting) { return; }

        // See if the menu is loaded - otherwize reload
        const meetings = getState().rds.rdsMeetings.filter((m) =>
            m.dateKey === getDateKey(meeting.startDate) && m.categoryCode === meeting.categoryCode
        )
        // To avoid multiple callbacks on every screen navigation, only load the side menu if not loaded.
        // (don't refresh all the time)
        if (meetings.length <= 1) {
            dispatch(loadRdsMeetings({
                date: meeting.startDate,
                categoryCode: meeting.categoryCode,
                meetingIdsToLoadEvents: [meeting.id]
            }));
        }
    };

///////////////////////////////////////////////////////////////////////////////////////////////////
// Schedule Events
///////////////////////////////////////////////////////////////////////////////////////////////////
export const removeScheduleEventsLoaded = createAsyncThunk(
    'schedule/removeScheduleEventsLoaded',
    async () => {
        return;
    }
);

export const loadScheduleEvents = (meetingId: number): AppThunk =>
    async (dispatch, getState) => {

        const events = getState().rds.rdsEvents.filter((e) => e.meetingId === meetingId);
        if (events) {
            events.forEach((event) => {
                if (mustReloadScheduleEvent(event, getState().schedule)) {
                    dispatch(loadScheduleEvent(event.id));
                }
            });
        }
    };

const mustReloadScheduleEvent = (event: rdsEvent_Dto, scheduleState: ScheduleState) => {
    const eventLoaded = scheduleState.scheduleEventsLoaded.find((el) => el === event.id);
    if (eventLoaded) {
        return false;  // Already loaded, don't need to reload
    }
    if (dateInReloadRange(event.dateTime)) {
        return true;  // Always reload
    }
    return !scheduleState.scheduleEvents.some((se) => se.eventId === event.id);
}

export const loadScheduleEvent = createAsyncThunk(
    'schedule/loadScheduleEvent',
    async (eventId: number) => {
        // TODO: This is where we call the api to get pricing data for the event
        // Sample data for now
        const scheduleEvent: ScheduleEvent = {
            eventId,
            pricingStatusCode: (eventId % 2) ? 'P' : 'N'
        }
        return scheduleEvent;
    }
);

///////////////////////////////////////////////////////////////////////////////////////////////////
// Reload from Event
///////////////////////////////////////////////////////////////////////////////////////////////////
export const reloadScheduleEvents = (meetingId: number): AppThunk =>
    async (dispatch, getState) => {

        await actionScheduleForItem(dispatch, getState().schedule, async () => {

            await dispatch(loadRdsEvents(meetingId))

            const events = getState().rds.rdsEvents.filter((e) => e.meetingId === meetingId);
            const eventFuncs = events?.map((e) => dispatch(
                loadScheduleEvent(e.id))
            );
            await Promise.allSettled(eventFuncs);

        })
    };

///////////////////////////////////////////////////////////////////////////////////////////////////
// Load action templates
///////////////////////////////////////////////////////////////////////////////////////////////////
const actionScheduleForSideMenu = async (
    // allow parameter to be any
    // eslint-disable-next-line
    dispatch: ThunkDispatch<any, any, any>,
    scheduleState: ScheduleState,
    process: () => Promise<void>
) => {
    await actionReducerItems(dispatch, scheduleState.loadingSideMenu, setLoadingSideMenu, process);
}

const actionScheduleForItem = async (
    // allow parameter to be any
    // eslint-disable-next-line
    dispatch: ThunkDispatch<any, any, any>,
    scheduleState: ScheduleState,
    process: () => Promise<void>
) => {
    await actionReducerItems(dispatch, scheduleState.loading, setLoading, process);
}

///////////////////////////////////////////////////////////////////////////////////////////////////
// Other
///////////////////////////////////////////////////////////////////////////////////////////////////

export const setLoading = createAsyncThunk(
    'schedule/setLoading',
    async (loading: boolean) => {
        return loading;
    }
);

export const setLoadingSideMenu = createAsyncThunk(
    'schedule/setLoadingSideMenu',
    async (loading: boolean) => {
        return loading;
    }
);
